import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, Building2, User as UserIcon, Mail, Phone, MapPin, Plus, Trash2, Star, FileText } from "lucide-react";
import { useClient, useDeleteClient, useAddContact, useDeleteContact, displayName } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import { downloadFile } from "@/utils/downloadFile";
import EditClientModal from "./EditClientModal";
import DocumentsSection from "./DocumentsSection";
import TasksSection from "./TasksSection";
import ServicesSection from "./ServicesSection";
import ProceduresSection from "./ProceduresSection";
import TaxesSection from "./TaxesSection";
import LegalCasesSection from "./LegalCasesSection";
import AccountingSection from "./AccountingSection";
import EmployeesSection from "./EmployeesSection";
import InvoicesSection from "./InvoicesSection";

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: "Activo",
  PROSPECT: "Prospecto",
  INACTIVE: "Inactivo",
  ARCHIVED: "Archivado",
};

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: client, isLoading } = useClient(id);
  const deleteClient = useDeleteClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);

  if (isLoading) return <p className="text-sm text-gray-400">Cargando...</p>;
  if (!client) return <p className="text-sm text-gray-400">Cliente no encontrado.</p>;

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar a "${displayName(client)}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteClient.mutateAsync(client.id);
      toast.success("Cliente eliminado.");
      navigate("/clients");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleServiceContract = async () => {
    try {
      await downloadFile(`/clients/${client.id}/contrato.docx`, `contrato-${displayName(client)}.docx`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft size={14} /> Volver a clientes
        </Link>
      </div>

      <div className="card flex flex-wrap items-start justify-between gap-4 p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600">
            {client.clientType === "PERSONA_JURIDICA" ? <Building2 size={24} /> : <UserIcon size={24} />}
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{displayName(client)}</h1>
            <p className="text-sm text-gray-500">
              {client.documentType} {client.documentNumber}
              {client.verificationDigit ? `-${client.verificationDigit}` : ""}
            </p>
            <span className="mt-1 inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
              {STATUS_LABEL[client.status]}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {hasPermission("clients.read") && (
            <button type="button" className="btn-secondary" onClick={handleServiceContract} title="Generar contrato de prestación de servicios (Word)">
              <FileText size={16} className="mr-1.5" /> Contrato (Word)
            </button>
          )}
          {hasPermission("clients.update") && (
            <button type="button" className="btn-secondary" onClick={() => setShowEdit(true)}>
              Editar
            </button>
          )}
          {hasPermission("clients.delete") && (
            <button type="button" className="btn-secondary text-red-600 hover:bg-red-50" onClick={handleDelete}>
              Eliminar
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card p-6 lg:col-span-2">
          <h2 className="text-sm font-semibold text-gray-900">Información general</h2>
          <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <InfoRow icon={Mail} label="Correo" value={client.email} />
            <InfoRow icon={Phone} label="Teléfono" value={client.phone || client.mobile} />
            <InfoRow icon={MapPin} label="Ciudad" value={[client.city, client.state, client.country].filter(Boolean).join(", ")} />
            <InfoRow label="Actividad económica" value={client.economicActivity} />
            <InfoRow label="Código CIIU" value={client.ciiuCode} />
            <InfoRow label="Régimen tributario" value={client.taxRegime} />
            {client.clientType === "PERSONA_JURIDICA" && (
              <InfoRow label="Representante legal" value={client.legalRepresentativeName} />
            )}
            <InfoRow label="Responsable interno" value={client.assignedTo?.fullName ?? "Sin asignar"} />
          </dl>

          {client.notes && (
            <div className="mt-6 border-t border-gray-100 pt-4">
              <h3 className="text-xs font-semibold uppercase text-gray-400">Notas internas</h3>
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-600">{client.notes}</p>
            </div>
          )}
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Contactos</h2>
            {hasPermission("clients.update") && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
                onClick={() => setShowAddContact(true)}
              >
                <Plus size={14} /> Agregar
              </button>
            )}
          </div>

          <div className="mt-4 space-y-3">
            {(!client.contacts || client.contacts.length === 0) && (
              <p className="text-sm text-gray-400">Sin contactos registrados.</p>
            )}
            {client.contacts?.map((contact) => (
              <ContactRow key={contact.id} clientId={client.id} contact={contact} canEdit={hasPermission("clients.update")} />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ServicesSection clientId={client.id} />
        <TasksSection clientId={client.id} />
        <ProceduresSection clientId={client.id} />
        <TaxesSection clientId={client.id} />
        <LegalCasesSection clientId={client.id} />
        <AccountingSection clientId={client.id} />
        <EmployeesSection clientId={client.id} />
        <InvoicesSection clientId={client.id} />
        <DocumentsSection clientId={client.id} />
      </div>

      <div className="card border-dashed p-5">
        <p className="text-sm text-gray-500">
          Comunicaciones de este cliente se irán habilitando aquí mismo a
          medida que se construyan sus módulos correspondientes.
        </p>
      </div>

      {showEdit && <EditClientModal client={client} onClose={() => setShowEdit(false)} />}
      {showAddContact && <AddContactModal clientId={client.id} onClose={() => setShowAddContact(false)} />}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon?: typeof Mail; label: string; value?: string | null }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon size={15} className="mt-0.5 shrink-0 text-gray-400" />}
      <div>
        <dt className="text-xs text-gray-400">{label}</dt>
        <dd className="text-sm text-gray-800">{value || "—"}</dd>
      </div>
    </div>
  );
}

function ContactRow({
  clientId,
  contact,
  canEdit,
}: {
  clientId: string;
  contact: { id: string; fullName: string; position: string | null; phone: string | null; email: string | null; isPrimary: boolean };
  canEdit: boolean;
}) {
  const deleteContact = useDeleteContact();

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar al contacto "${contact.fullName}"?`)) return;
    try {
      await deleteContact.mutateAsync({ clientId, contactId: contact.id });
      toast.success("Contacto eliminado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="flex items-start justify-between rounded-md border border-gray-100 p-3">
      <div>
        <p className="flex items-center gap-1 text-sm font-medium text-gray-900">
          {contact.fullName}
          {contact.isPrimary && <Star size={12} className="fill-amber-400 text-amber-400" />}
        </p>
        {contact.position && <p className="text-xs text-gray-500">{contact.position}</p>}
        {contact.email && <p className="text-xs text-gray-500">{contact.email}</p>}
        {contact.phone && <p className="text-xs text-gray-500">{contact.phone}</p>}
      </div>
      {canEdit && (
        <button type="button" className="text-gray-400 hover:text-red-600" onClick={handleDelete} aria-label="Eliminar contacto">
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

function AddContactModal({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const addContact = useAddContact();
  const [fullName, setFullName] = useState("");
  const [position, setPosition] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addContact.mutateAsync({ clientId, fullName, position, email, phone, isPrimary });
      toast.success("Contacto agregado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900">Nuevo contacto</h2>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <input
            className="input-field"
            placeholder="Nombre completo"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
          <input className="input-field" placeholder="Cargo" value={position} onChange={(e) => setPosition(e.target.value)} />
          <input
            type="email"
            className="input-field"
            placeholder="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input className="input-field" placeholder="Teléfono" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
            Contacto principal
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={addContact.isPending}>
              {addContact.isPending ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
